import { useCallback, useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, PlayIcon } from 'lucide-react';
import { BackLink, Button, ErrorNotice, PageEyebrow, Panel } from '@bcecloud/portal-ui';
import { MarkdownArticle } from '@/components/MarkdownArticle';
import { findDoc, helpAreas, isDocVisible } from '@/help/content';
import { useFeatureLookup } from '@/lib/app-features';
import { usePermissionLookup } from '@/lib/use-permission-lookup';
import { useGuide } from '@/guides/use-guide';
import { guidePath } from '@/guides/runner';
import { GUIDES, isGuideId } from '@/guides/registry';
import { enterSandbox } from '@/sandbox/mode';
import { useLocation, useNavigate } from 'react-router-dom';

/** One help page: the article, a Show me button when it has a guide, and the area's other pages beside it. */
export function HelpArticlePage(): React.ReactElement {
  const { area = '', slug = '' } = useParams<{ area: string; slug: string }>();
  const [params] = useSearchParams();
  const isFeatureOn = useFeatureLookup();
  const hasPermission = usePermissionLookup();
  // A page about a switched-off feature, or behind a grant the reader lacks, reads as missing.
  const found = findDoc(area, slug);
  const doc = found && isDocVisible(found, isFeatureOn, hasPermission) ? found : undefined;
  const runGuide = useGuide();
  // A tour behind a grant is offered only to people who hold it, as its screens are.
  const canRunGuide = useCallback(
    (id: string) => {
      const permission = isGuideId(id) ? GUIDES[id].permission : undefined;
      return permission === undefined || hasPermission(permission);
    },
    [hasPermission],
  );
  const navigate = useNavigate();
  const location = useLocation();
  const siblings = helpAreas().find((candidate) => candidate.key === area);
  const siblingDocs = (siblings?.docs ?? []).filter((other) => isDocVisible(other, isFeatureOn, hasPermission));
  // The area's pages in reading order, for Previous and Next at the foot of the page.
  const position = siblingDocs.findIndex((other) => other.path === doc?.path);
  const previous = position > 0 ? siblingDocs[position - 1] : undefined;
  const next = position >= 0 ? siblingDocs[position + 1] : undefined;
  const startGuide = doc?.guide && params.get('guide') === '1' && canRunGuide(doc.guide) ? doc.guide : null;
  // Previous and Next keep this page mounted and only change the route, so
  // the browser would stay scrolled to the foot of the last page. Start each
  // page at the top.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [area, slug]);

  if (!doc) {
    return (
      <section className="animate-portal-rise">
        <BackLink to="/help">All help</BackLink>
        <div className="mt-4">
          <ErrorNotice title="No such page" message="It may have moved. Head back to Help and search for it." />
        </div>
      </section>
    );
  }

  return (
    <section className="animate-portal-rise">
      <BackLink to="/help">All help</BackLink>
      <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_260px]">
        <div className="min-w-0">
          <header className="mb-5">
            <PageEyebrow>{siblings?.label ?? area}</PageEyebrow>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h1 className="text-portal-page tracking-[-1.7px]">{doc.title}</h1>
              {doc.guide && canRunGuide(doc.guide) ? (
                <span className="flex flex-col items-end gap-1">
                  <Button variant="secondary" onClick={() => runGuide(doc.guide ?? '')}>
                    <PlayIcon aria-hidden className="size-4" /> Show me
                  </Button>
                  {isGuideId(doc.guide) ? (
                    <button
                      type="button"
                      className="text-portal-micro font-semibold text-brand hover:underline"
                      onClick={() => {
                        const guide = GUIDES[doc.guide as keyof typeof GUIDES];
                        enterSandbox(`${location.pathname}${location.search}`);
                        navigate(guidePath(guide.path));
                      }}
                    >
                      Try it in the sandbox
                    </button>
                  ) : null}
                </span>
              ) : null}
            </div>
            {doc.updated ? <p className="mt-1 text-portal-micro text-portal-muted">Updated {doc.updated}</p> : null}
          </header>
          {doc.video ? (
            <figure className="mb-5 overflow-hidden rounded-portal-card border border-portal-line bg-black shadow-portal-card">
              {/* Captions are burned into the video, so there is no separate track. */}
              <video
                key={doc.video.src}
                className="block aspect-video w-full"
                src={doc.video.src}
                poster={doc.video.poster}
                controls
                preload="metadata"
                playsInline
                aria-label={`${doc.title}: walkthrough video`}
              />
            </figure>
          ) : null}
          <Panel className="p-6">
            <MarkdownArticle markdown={doc.markdown} onGuide={runGuide} canRunGuide={canRunGuide} />
          </Panel>
          {previous || next ? (
            <nav aria-label={`More in ${siblings?.label ?? 'this area'}`} className="mt-4 grid gap-3 sm:grid-cols-2">
              {previous ? (
                <Link to={previous.path} className="group rounded-portal-card border border-portal-line bg-white p-4 shadow-portal-card hover:border-brand">
                  <span className="flex items-center gap-1 text-portal-micro font-bold text-portal-muted">
                    <ArrowLeftIcon aria-hidden className="size-3.5" /> Previous
                  </span>
                  <span className="mt-1 block text-portal-small font-bold text-portal-ink group-hover:text-brand">{previous.title}</span>
                </Link>
              ) : (
                <span aria-hidden className="hidden sm:block" />
              )}
              {next ? (
                <Link to={next.path} className="group rounded-portal-card border border-portal-line bg-white p-4 text-right shadow-portal-card hover:border-brand">
                  <span className="flex items-center justify-end gap-1 text-portal-micro font-bold text-portal-muted">
                    Next <ArrowRightIcon aria-hidden className="size-3.5" />
                  </span>
                  <span className="mt-1 block text-portal-small font-bold text-portal-ink group-hover:text-brand">{next.title}</span>
                </Link>
              ) : null}
            </nav>
          ) : null}
          {startGuide ? <AutoStart guideId={startGuide} run={runGuide} /> : null}
        </div>
        {siblingDocs.length > 1 ? (
          <aside className="hidden lg:block">
            <p className="mb-2 text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">{siblings?.label}</p>
            <ul className="space-y-1">
              {siblingDocs.map((other) => (
                <li key={other.path}>
                  <Link to={other.path} className={other.path === doc.path ? 'block text-portal-small font-bold text-brand' : 'block text-portal-small text-portal-muted hover:text-brand'}>
                    {other.title}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </div>
    </section>
  );
}

/** Runs a guide once when the page was opened with ?guide=1 (the "Take the tour" link). */
function AutoStart({ guideId, run }: { readonly guideId: string; readonly run: (id: string) => void }): null {
  useAutoStart(guideId, run);
  return null;
}

function useAutoStart(guideId: string, run: (id: string) => void): void {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    run(guideId);
  }, [guideId, run]);
}
